import { useState, useEffect, useRef } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  billingService,
  BillingInvoice,
  BillingDocument,
  type BillingInvoiceListResponse,
  type BillingDocumentListResponse,
} from '../../services/billing.service';
import { destroyDataTableIfPresent } from '../../utils/dataTableLifecycle';

type BillingListResponse = BillingInvoiceListResponse | BillingDocumentListResponse;
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
// Note: DataTables CSS is loaded from local files in index.html (/vendor/datatables/css/)

type TableMode = 'invoices' | 'documents';

interface InvoiceTableProps {
  mode?: TableMode;
  documentTypeFilter?: 'quote' | 'delivery' | 'invoice';
  isAdmin?: boolean;
  onInvoiceClick?: (invoice: BillingInvoice) => void;
  onDocumentClick?: (document: BillingDocument) => void;
}

export default function InvoiceTable({
  mode = 'invoices',
  documentTypeFilter,
  isAdmin = false,
  onInvoiceClick,
  onDocumentClick,
}: InvoiceTableProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);
  const dataTableRef = useRef<any>(null);

  const { data, isLoading, isError } = useQuery<BillingListResponse>({
    queryKey: ['billing', mode, documentTypeFilter],
    queryFn: async (): Promise<BillingListResponse> =>
      mode === 'documents'
        ? billingService.getDocuments({ page: 1, limit: 1000, type: documentTypeFilter })
        : billingService.getInvoices({ page: 1, limit: 1000 }),
  });

  useEffect(() => {
    if (!tableRef.current || !data?.data || isLoading) return;

    try {
      if (
        typeof window === 'undefined' ||
        !(window as any).jQuery ||
        !(window as any).jQuery.fn.DataTable
      ) {
        setError(
          t('common.datatables_not_loaded', {
            defaultValue:
              'DataTablesが読み込まれていません。jQueryとDataTablesを読み込んでください。',
          })
        );
        return;
      }

      const $ = (window as any).jQuery;

      destroyDataTableIfPresent(tableRef.current);
      dataTableRef.current = null;

      const isDocuments = mode === 'documents';
      const dataRows = data.data;

      const documentColumns = [
            {
              data: 'documentNumber',
              title: t('billing.document_number', { defaultValue: '書類番号' }),
            },
            {
              data: 'type',
              title: t('billing.document_type', { defaultValue: '種別' }),
              render: (value: string) => {
                const labels: Record<string, string> = {
                  quote: t('billing.document_type_quote', { defaultValue: '見積書' }),
                  delivery: t('billing.document_type_delivery', { defaultValue: '納品書' }),
                  invoice: t('billing.document_type_invoice', { defaultValue: '請求書' }),
                };
                return labels[value] || value;
              },
            },
            {
              data: 'status',
              title: t('billing.invoice_status', { defaultValue: 'ステータス' }),
              render: (value: string) => {
                const variants: Record<string, string> = {
                  draft: 'secondary',
                  pending_approval: 'warning',
                  approved: 'info',
                  issued: 'success',
                  rejected: 'danger',
                  void: 'secondary',
                };
                const variant = variants[value?.toLowerCase()] || 'secondary';
                return `<span class="badge bg-${variant}">${value}</span>`;
              },
            },
            {
              data: 'totalCentsUsd',
              title: t('billing.invoice_total_amount', { defaultValue: '合計金額' }),
              className: 'text-end',
              render: (value: number) => `$${(value / 100).toFixed(2)}`,
            },
            {
              data: 'issuedAt',
              title: t('billing.invoice_issued_at', { defaultValue: '発行日' }),
              defaultContent: '-',
              render: (value: string) => {
                if (!value) return '-';
                try {
                  return format(new Date(value), t('common.date_format_iso'), { locale: ja });
                } catch {
                  return value;
                }
              },
            },
            {
              data: 'dueAt',
              title: t('billing.invoice_due_at', { defaultValue: '支払期限' }),
              defaultContent: '-',
              render: (value: string) => {
                if (!value) return '-';
                try {
                  return format(new Date(value), t('common.date_format_iso'), { locale: ja });
                } catch {
                  return value;
                }
              },
            },
            {
              data: null,
              title: t('common.actions', { defaultValue: '操作' }),
              className: 'text-center',
              orderable: false,
              defaultContent: '',
              render: (_data: any, _type: any, row: BillingDocument) => {
                const canDownload = ['issued', 'void'].includes(row.status);
                return `
                  <button class="btn btn-sm btn-info me-1 view-btn" data-id="${row.id}">${t('common.view', { defaultValue: '詳細' })}</button>
                  ${
                    canDownload
                      ? `<button class="btn btn-sm btn-primary download-btn" data-id="${row.id}" data-number="${row.documentNumber}">PDF</button>`
                      : ''
                  }
                `;
              },
            },
          ];

      const columns = isDocuments
        ? [
            ...documentColumns.slice(0, 2),
            ...(isAdmin
              ? [
                  {
                    data: 'user',
                    title: t('billing.document_issue_history_col_recipient', {
                      defaultValue: '発行先',
                    }),
                    render: (_value: unknown, _type: unknown, row: BillingDocument) => {
                      const user = row.user;
                      if (!user) return '-';
                      const fullName = [user.lastName, user.firstName].filter(Boolean).join(' ');
                      return (
                        fullName ||
                        user.organizationName ||
                        user.username ||
                        user.email ||
                        t('common.unknown', { defaultValue: '不明' })
                      );
                    },
                  },
                ]
              : []),
            ...documentColumns.slice(2),
          ]
        : [
            {
              data: 'invoiceNumber',
              title: t('billing.invoice_number', { defaultValue: '請求書番号' }),
            },
            {
              data: 'status',
              title: t('billing.invoice_status', { defaultValue: 'ステータス' }),
              render: (value: string) => {
                const variants: Record<string, string> = {
                  paid: 'success',
                  unpaid: 'warning',
                  past_due: 'danger',
                  void: 'secondary',
                };
                const variant = variants[value.toLowerCase()] || 'secondary';
                return `<span class="badge bg-${variant}">${value}</span>`;
              },
            },
            {
              data: 'totalCentsUsd',
              title: t('billing.invoice_total_amount', { defaultValue: '合計金額' }),
              className: 'text-end',
              render: (value: number) => {
                return `$${(value / 100).toFixed(2)}`;
              },
            },
            {
              data: 'issuedAt',
              title: t('billing.invoice_issued_at', { defaultValue: '発行日' }),
              defaultContent: '-',
              render: (value: string) => {
                if (!value) return '-';
                try {
                  return format(new Date(value), t('common.date_format_iso'), { locale: ja });
                } catch {
                  return value;
                }
              },
            },
            {
              data: 'dueAt',
              title: t('billing.invoice_due_at', { defaultValue: '支払期限' }),
              defaultContent: '-',
              render: (value: string) => {
                if (!value) return '-';
                try {
                  return format(new Date(value), t('common.date_format_iso'), { locale: ja });
                } catch {
                  return value;
                }
              },
            },
            {
              data: 'paidAt',
              title: t('billing.invoice_paid_at', { defaultValue: '支払日' }),
              defaultContent: '-',
              render: (value: string) => {
                if (!value) return '-';
                try {
                  return format(new Date(value), t('common.date_format_iso'), { locale: ja });
                } catch {
                  return value;
                }
              },
            },
            {
              data: null,
              title: t('common.actions', { defaultValue: '操作' }),
              className: 'text-center',
              orderable: false,
              defaultContent: '',
              render: (_data: any, _type: any, row: BillingInvoice) => {
                const canDownload = !!row.pdfPath;
                return `
                  <button class="btn btn-sm btn-info me-1 view-btn" data-id="${row.id}">${t('common.view', { defaultValue: '詳細' })}</button>
                  ${
                    canDownload
                      ? `<button class="btn btn-sm btn-primary download-btn" data-id="${row.id}" data-number="${row.invoiceNumber}">PDF</button>`
                      : ''
                  }
                `;
              },
            },
          ];

      dataTableRef.current = $(tableRef.current).DataTable({
        data: dataRows,
        columns,
        language: {
          url: '/vendor/datatables/i18n/ja.json',
        },
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
        order: [[0, 'desc']],
        responsive: true,
        autoWidth: false,
      });

      if (onInvoiceClick || onDocumentClick) {
        $(tableRef.current).on('click', '.view-btn', function (this: HTMLElement, e: JQuery.Event) {
          e.preventDefault();
          const id = $(this).data('id');
          if (isDocuments) {
            const document = (dataRows as BillingDocument[]).find((doc) => doc.id === id);
            if (document && onDocumentClick) {
              onDocumentClick(document);
            }
          } else {
            const invoice = (dataRows as BillingInvoice[]).find((inv) => inv.id === id);
            if (invoice && onInvoiceClick) {
              onInvoiceClick(invoice);
            }
          }
        });
      }

      $(tableRef.current).on(
        'click',
        '.download-btn',
        function (this: HTMLElement, e: JQuery.Event) {
          e.preventDefault();
          const id = $(this).data('id');
          const number = $(this).data('number');
          const downloadPromise = isDocuments
            ? billingService.downloadDocumentPdf(id)
            : billingService.downloadInvoicePdf(id);
          downloadPromise
            .then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = isDocuments
                ? `document_${number || id}.pdf`
                : `invoice_${number || id}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            })
            .catch((err) => {
              console.error('PDF download error:', err);
              alert(
                t('billing.invoice_download_failed', {
                  defaultValue: 'PDFのダウンロードに失敗しました',
                })
              );
            });
        }
      );
    } catch (err) {
      console.error('DataTables initialization error:', err);
      setError(t('common.table_init_failed', { defaultValue: 'テーブルの初期化に失敗しました' }));
    }

    return () => {
      const tableEl = tableRef.current;
      const dt = dataTableRef.current;
      try {
        if (tableEl && typeof window !== 'undefined' && (window as any).jQuery) {
          const $ = (window as any).jQuery;
          $(tableEl).off('click', '.view-btn');
          $(tableEl).off('click', '.download-btn');
        }
        const parentExists = !!tableEl?.parentNode;
        if (dt && tableEl && parentExists && document.body.contains(tableEl)) {
          dt.destroy(false);
        }
        destroyDataTableIfPresent(tableEl);
      } catch (err) {
        console.error('DataTables破棄エラー:', err);
      } finally {
        dataTableRef.current = null;
      }
    };
  }, [data, isLoading, onInvoiceClick, onDocumentClick, mode, documentTypeFilter, isAdmin, t]);

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </Spinner>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mt-4">
        {t('billing.invoices_load_failed', { defaultValue: '請求書データの取得に失敗しました' })}
      </Alert>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <div className="table-responsive">
        <table
          ref={tableRef}
          className="table table-striped table-hover table-bordered"
          style={{ width: '100%' }}
        >
          <thead></thead>
          <tbody></tbody>
        </table>
      </div>
    </>
  );
}
